import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import * as _ from "lodash";
import { EditActions, editActions } from "../../actions/editActions";
import Editor from "../../components/Editing/Editor";
import TextField from "../../components/Editing/TextField";
import CheckboxField from "../../components/Editing/CheckboxField";
import { hasWriteAccess, nameofFactory } from "../../utils/utils";

type StateProps = {
  editedEnumeration: Api.CommonEnumeration;
  storedEnumeration: Api.CommonEnumeration;
  pdApi: State.PdApi;
  activeUser: Api.User | undefined;
};

type DispatchProps = {
  editActions: typeof editActions;
};

type OwnProps = {
  toggleModal: () => void;
};

const enumerationFields = nameofFactory<Api.CommonEnumeration>();

export type EditEnumerationProps = StateProps & DispatchProps & OwnProps;

export class EditEnumeration extends React.Component<EditEnumerationProps> {
  render(): JSX.Element {
    const { editedEnumeration, storedEnumeration, activeUser, toggleModal } = this.props;
    const hasChild = editedEnumeration.id !== undefined && editedEnumeration.enumerationItems?.length > 0;
    const isNew = !storedEnumeration;
    const editorTitle = "enumeration";
    const deleteDisabled = isNew;

    return (
      <Editor
        itemName={editorTitle}
        originalObject={storedEnumeration}
        editedObject={editedEnumeration}
        hasWritePermission={hasWriteAccess(activeUser)}
        isValid={this.isAllFieldsValid()}
        cancel={this.props.editActions.cancelEditing}
        update={() => this.props.editActions.update("enumerations")}
        create={() => this.props.editActions.add("enumerations")}
        remove={{
          callback: toggleModal,
          enabledTooltip: "common enumeration",
          disabled: deleteDisabled || hasChild,
          disabledTooltip: ": Item has linked child enumeration items."
        }}
      >
        <CheckboxField
          id={"localized"}
          label={"Localized"}
          checked={editedEnumeration.isLocalized}
          onChange={value =>
            this.props.editActions.setEdited({ ...editedEnumeration, isLocalized: value })
          }
        />
        <TextField
          id="target"
          label="Target"
          value={editedEnumeration.target}
          isError={!this.isFieldValid(enumerationFields("target"))}
          onChange={target => {
            this.props.editActions.setEdited({ ...editedEnumeration, target });
          }}
        />
        <TextField
          id="name"
          label="Name"
          value={editedEnumeration.name}
          isError={!this.isFieldValid(enumerationFields("name"))}
          errorMessage={!this.isEmptyString(editedEnumeration.name) ? "The name must be unique in the target." : ""}
          onChange={enumName =>
            this.props.editActions.setEdited({
              ...editedEnumeration,
              name: enumName
            })
          }
        />
        <TextField
          id="comment"
          label="Comment"
          value={editedEnumeration.comment}
          onChange={comment =>
            this.props.editActions.setEdited({
              ...editedEnumeration,
              comment
            })
          }
        />
      </Editor>
    );
  }

  private isFieldValid = (field: string): boolean => {
    const { pdApi, editedEnumeration, storedEnumeration } = this.props;
    const isNew = !storedEnumeration;
    let retval = true;
    switch (field) {
      case enumerationFields("target"):
        {
          const reserved = pdApi.enumerations.map(x => x.target);
          const notEmpty = !this.isEmptyString(editedEnumeration.target);
          const unchangedEdit = !isNew && editedEnumeration.target === storedEnumeration.target;
          retval = reserved ? notEmpty || unchangedEdit : true;
        }
        break;
      case enumerationFields("name"):
        {
          const reserved = pdApi.enumerations
            .filter(x => x.target === editedEnumeration.target)
            .map(x => x.name);
          const notEmpty = !this.isEmptyString(editedEnumeration.name);
          const unique = !reserved?.includes(editedEnumeration.name);
          const unchangedEdit = !isNew && editedEnumeration.name === storedEnumeration.name && editedEnumeration.target === storedEnumeration.target;
          retval = reserved ? (notEmpty && unique) || unchangedEdit : true;
        }
        break;
      default:
        return true;
    }
    return retval;
  };

  private isEmptyString = (str: string): boolean => !(!!str.trim() && str.trim().length > 0);

  private isAllFieldsValid = (): boolean => {
    const { editedEnumeration } = this.props;
    const temp = _.keys(editedEnumeration);
    for (const key of temp) {
      if (!this.isFieldValid(key)) {
        return false;
      }
    }
    return true;
  };
}

const mapStateToProps = (state: State.Root) => ({
  editedEnumeration: state.editView.editedObject,
  storedEnumeration: state.editView.storedObject,
  pdApi: state.pdApi,
  activeUser: state.main.activeUser
});

const mapDispatchToProps = (dispatch: Dispatch<EditActions>) => ({
  editActions: bindActionCreators({ ...editActions }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(EditEnumeration);


